'use client'

import Image from 'next/image'
import { useState, useEffect, useRef, useMemo } from 'react'
import Link from 'next/link'
import { useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import { useForm, useFieldArray, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  ImageIcon,
  MapPinIcon,
  MailIcon,
  PackageIcon,
  PlusIcon,
  SaveIcon,
  ShoppingCartIcon,
  StickyNoteIcon,
  Trash2Icon,
  UserIcon,
  XIcon
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { MoneyInput } from '@/components/ui/money-input'
import { PhoneInput } from '@/components/ui/phone-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { UserCombobox } from '@/components/ui/user-combobox'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { Combobox } from '@/components/ui/combobox'
import { AddressFormDialog } from '@/components/partials/users/address-form-dialog'
import { DeleteConfirmationDialog, UnsavedChangesGuardDialog } from '@/components/partials'
import { AddOrderItemDialog } from './add-order-item-dialog'
import {
  useCreateGuestOrder,
  useCreateRegisteredOrder,
  useUserAddresses,
  useCreateAddress,
  useCountries,
  useStates,
  useCities,
  useScrollToError,
  useUser,
  useUnsavedChangesGuard
} from '@/hooks'
import { FormModeEnum, OrderStatusEnum, SortEnum } from '@/enums'
import { CreateAddressParams } from '@/types/address'
import { cn, getErrorMessage, getResizedPictureUrl } from '@/lib/utils'
import { useMetaStore } from '@/store'
import { fetchExchangeRates } from '@/lib/api'

// ── Form Schema ──────────────────────────────────────────────────────────────

const buildSchema = (t: ReturnType<typeof useTranslations>) =>
  z
    .object({
      customerType: z.enum(['registered', 'guest'] as const),
      userId: z.string().optional(),
      addressId: z.string().optional(),
      firstname: z.string().optional(),
      lastname: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      countryId: z.string().optional(),
      stateId: z.string().optional(),
      cityId: z.string().optional(),
      zipCode: z.string().optional(),
      addressText: z.string().optional(),
      currencyCode: z.string().min(1, t('not_blank')),
      status: z.enum([
        OrderStatusEnum.PENDING,
        OrderStatusEnum.CONFIRMED,
        OrderStatusEnum.PROCESSING,
        OrderStatusEnum.SHIPPED,
        OrderStatusEnum.DELIVERED,
        OrderStatusEnum.CANCELLED,
        OrderStatusEnum.REFUNDED
      ]),
      items: z
        .array(
          z.object({
            productId: z.string().optional(),
            productCode: z.string(),
            productTitle: z.string(),
            mainPicturePath: z.string().optional(),
            quantity: z.number().int().min(1),
            unitPriceOverride: z.number().optional(),
            discountRateOverride: z.number().min(0).max(100).optional(),
            unitNetPrice: z.number().optional(),
            originalGross: z.number().optional(),
            taxRate: z.number().optional(),
            priceIncludesTax: z.boolean().optional(),
            // Tracks whether admin explicitly changed the unit price for a registered product.
            // When false, unitPriceOverride is used for display only and NOT sent to the backend,
            // so the backend can apply the product's original price with the discount intact.
            priceManuallyOverridden: z.boolean().optional()
          })
        )
        .min(1, t('min_value', { value: 1 })),
      discountRate: z.number().min(0).max(100).optional(),
      note: z.string().optional(),
      noteIsPublic: z.boolean(),
      notifyCustomer: z.boolean(),
      showPrices: z.boolean(),
      locale: z.string().optional()
    })
    .superRefine((data, ctx) => {
      if (data.customerType === 'registered') {
        if (!data.userId) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['userId'] })
        }
        if (!data.addressId) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['addressId'] })
        }
      } else {
        if (!data.firstname?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['firstname'] })
        }
        if (!data.lastname?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['lastname'] })
        }
        if (!data.email?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['email'] })
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
          ctx.addIssue({ code: 'custom', message: t('invalid_email'), path: ['email'] })
        }
        if (!data.countryId?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['countryId'] })
        }
        if (!data.stateId?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['stateId'] })
        }
        if (!data.cityId?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['cityId'] })
        }
        if (!data.addressText?.trim()) {
          ctx.addIssue({ code: 'custom', message: t('not_blank'), path: ['addressText'] })
        }
      }
    })

type FormData = z.infer<ReturnType<typeof buildSchema>>

// ── OrderCreate ──────────────────────────────────────────────────────────────

const OrderCreate = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()

  const [addItemOpen, setAddItemOpen] = useState(false)
  const [deleteItemIndex, setDeleteItemIndex] = useState<number | null>(null)
  const [addAddressOpen, setAddAddressOpen] = useState(false)

  const { metaState } = useMetaStore()
  const defaultCurrencyCode = metaState.response?.generalSettings.defaultCurrencyCode ?? ''
  const activeCurrencies = metaState.response?.currencies.filter(c => c.active) ?? []
  const activeLocales = metaState.response?.locales.filter(l => l.active) ?? []
  const defaultLocaleCode = metaState.response?.generalSettings.appDefaultLocale ?? ''

  const form = useForm<FormData>({
    resolver: zodResolver(buildSchema(t)),
    defaultValues: {
      customerType: 'registered',
      userId: '',
      addressId: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      countryId: '',
      stateId: '',
      cityId: '',
      zipCode: '',
      addressText: '',
      status: OrderStatusEnum.PENDING,
      currencyCode: defaultCurrencyCode,
      items: [],
      discountRate: undefined,
      note: '',
      noteIsPublic: false,
      notifyCustomer: false,
      showPrices: false,
      locale: defaultLocaleCode
    }
  })

  const { isNavigationBlocked, confirmNavigation, cancelNavigation } = useUnsavedChangesGuard(form.formState.isDirty)

  const {
    fields: itemFields,
    append: appendItem,
    remove: removeItem
  } = useFieldArray({
    control: form.control,
    name: 'items'
  })

  const customerType = form.watch('customerType')
  const userId = form.watch('userId')
  const countryId = form.watch('countryId')
  const stateId = form.watch('stateId')
  const watchedItems = useWatch({ control: form.control, name: 'items' }) ?? []
  const watchedDiscountRate = useWatch({ control: form.control, name: 'discountRate' })

  // Per-item computed display values — itemFields üzerinden hesapla (useWatch frame gecikmesinden bağımsız)
  const itemDisplayData = itemFields.map((field, index) => {
    const item = watchedItems[index] ?? field
    const basePrice = item.originalGross ?? item.unitPriceOverride ?? 0
    const discountRate = item.discountRateOverride ?? 0
    const discountedGross = discountRate > 0 ? basePrice * (1 - discountRate / 100) : basePrice
    const discountAmount = basePrice - discountedGross
    const taxRate = item.taxRate ?? 0
    const priceIncludesTax = item.priceIncludesTax ?? true
    let finalGross: number
    let netPrice: number
    if (priceIncludesTax) {
      finalGross = discountedGross
      netPrice = taxRate > 0 ? (discountedGross * 100) / (100 + taxRate) : discountedGross
    } else {
      netPrice = discountedGross
      finalGross = taxRate > 0 ? discountedGross * (1 + taxRate / 100) : discountedGross
    }
    const qty = item.quantity ?? 0
    return {
      basePrice,
      discountedGross,
      discountAmount,
      discountRate,
      netPrice,
      finalGross,
      subtotal: finalGross * qty
    }
  })

  const subtotals = itemDisplayData.map(d => d.subtotal)
  const netUnitPrices = itemDisplayData.map(d => d.netPrice)

  const itemsTotal = subtotals.reduce((sum, v) => sum + v, 0)
  const discountAmount = itemsTotal * ((watchedDiscountRate ?? 0) / 100)
  const grandTotal = itemsTotal - discountAmount

  const netSubtotals = itemDisplayData.map((d, i) => d.netPrice * (watchedItems[i]?.quantity ?? 0))
  const netItemsTotal = netSubtotals.reduce((sum, v) => sum + v, 0)
  const netDiscountAmount = netItemsTotal * ((watchedDiscountRate ?? 0) / 100)
  const netTotal = netItemsTotal - netDiscountAmount
  const taxTotal = grandTotal - netTotal

  const { data: addressesData, isLoading: isLoadingAddresses } = useUserAddresses(
    userId || '',
    { size: 50 },
    { enabled: !!userId }
  )
  const addresses = useMemo(() => addressesData?.items || [], [addressesData?.items])

  // Auto-select primary address when addresses are loaded and none is selected
  useEffect(() => {
    if (addresses.length > 0 && !form.getValues('addressId')) {
      const primary = addresses.find(a => a.primary) ?? addresses[0]
      form.setValue('addressId', primary.id)
    }
  }, [addresses, form])

  const { data: countries = [] } = useCountries()
  const { data: states = [], isLoading: isLoadingStates } = useStates(countryId || undefined)
  const { data: cities = [], isLoading: isLoadingCities } = useCities(stateId || undefined)

  const stateOptions = states.map(s => ({
    label: `${s.name} (${s.code})`,
    value: s.id,
    searchValue: `${s.name} ${s.code}`.toLowerCase()
  }))
  const cityOptions = cities.map(c => ({ label: c.name, value: c.id, searchValue: c.name.toLowerCase() }))

  const selectedCurrencyCode = form.watch('currencyCode')

  // Sync form currencyCode when defaultCurrencyCode becomes available (e.g., meta loaded async)
  useEffect(() => {
    if (defaultCurrencyCode && !form.getValues('currencyCode')) {
      form.setValue('currencyCode', defaultCurrencyCode)
    }
  }, [defaultCurrencyCode, form])

  const { data: userData } = useUser(userId || '')

  // Auto-set currency + discountRate when a user changes — but only once per userId selection.
  // Using a ref prevents re-triggering when userData loads async after the user manually changed currency.
  const autoSetCurrencyForUserRef = useRef<string>('')
  useEffect(() => {
    if (!userId) {
      autoSetCurrencyForUserRef.current = ''
      form.setValue('currencyCode', defaultCurrencyCode)
      form.setValue('discountRate', undefined)
      return
    }
    if (userData && autoSetCurrencyForUserRef.current !== userId) {
      autoSetCurrencyForUserRef.current = userId
      if (userData.currencyCode) {
        form.setValue('currencyCode', userData.currencyCode)
      }
      if (userData.discountRate !== undefined) {
        form.setValue('discountRate', userData.discountRate)
      }
    }
  }, [userId, userData, defaultCurrencyCode, form])

  const currencySymbol =
    activeCurrencies.find(c => c.code === selectedCurrencyCode)?.symbol || selectedCurrencyCode || ''

  const { mutate: createGuest, isPending: isCreatingGuest } = useCreateGuestOrder()
  const { mutate: createRegistered, isPending: isCreatingRegistered } = useCreateRegisteredOrder()
  const { mutate: createAddress, isPending: isCreatingAddress } = useCreateAddress()
  const isPending = isCreatingGuest || isCreatingRegistered
  const { scrollToFirstError } = useScrollToError()

  const handleSubmit = (data: FormData) => {
    const mappedItems = data.items.map(i => ({
      productId: i.productId || undefined,
      freeProductCode: !i.productId ? i.productCode : undefined,
      freeProductName: !i.productId ? i.productTitle : undefined,
      quantity: i.quantity,
      // For registered products, only send unitPriceOverride if admin explicitly changed the price.
      // Sending the default product price as an override disables backend discount tracking.
      unitPriceOverride: i.productId && !i.priceManuallyOverridden ? undefined : i.unitPriceOverride,
      discountRateOverride: i.discountRateOverride,
      taxRate: i.taxRate,
      priceIncludesTax: i.priceIncludesTax
    }))
    const noteParam = data.note?.trim() ? { note: data.note.trim(), noteIsPublic: data.noteIsPublic } : {}
    const discountParam = data.discountRate !== undefined ? { discountRate: data.discountRate } : {}

    if (data.customerType === 'guest') {
      const countryName = countries.find(c => c.id === data.countryId)?.name || data.countryId || ''
      const stateName = states.find(s => s.id === data.stateId)?.name
      const cityName = cities.find(c => c.id === data.cityId)?.name
      createGuest(
        {
          params: {
            firstname: data.firstname!,
            lastname: data.lastname!,
            email: data.email!,
            phone: data.phone || undefined,
            address: {
              country: countryName,
              state: stateName || undefined,
              city: cityName || undefined,
              zipCode: data.zipCode || undefined,
              addressText: data.addressText || undefined
            },
            items: mappedItems,
            status: data.status,
            notifyCustomer: data.notifyCustomer,
            showPrices: data.showPrices,
            locale: data.locale || undefined,
            ...discountParam,
            ...noteParam
          },
          currency: data.currencyCode || undefined
        },
        { onSuccess: order => router.push(`/orders/${order.id}`) }
      )
    } else {
      createRegistered(
        {
          params: {
            userId: data.userId!,
            addressId: data.addressId!,
            items: mappedItems,
            status: data.status,
            notifyCustomer: data.notifyCustomer,
            locale: data.locale || undefined,
            ...noteParam
          },
          currency: data.currencyCode || undefined
        },
        { onSuccess: order => router.push(`/orders/${order.id}`) }
      )
    }
  }

  const handleCreateAddress = (params: CreateAddressParams) => {
    createAddress(
      { userId: userId!, params },
      {
        onSuccess: async newAddress => {
          await queryClient.invalidateQueries({ queryKey: ['user-addresses', userId] })
          form.setValue('addressId', newAddress.id)
          setAddAddressOpen(false)
          toast.success(t('address_created'))
        },
        onError: error => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" asChild>
          <Link href="/orders">
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="hidden lg:inline">{t('back')}</span>
          </Link>
        </Button>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ShoppingCartIcon className="h-6 w-6" />
          {t('create_order')}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, scrollToFirstError)} className="space-y-6">
          {/* ── Card 1: Order Info ────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCartIcon className="h-5 w-5" />
                {t('order_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-4">
                {/* Customer Type */}
                <FormField
                  control={form.control}
                  name="customerType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel htmlFor={undefined} required>
                        {t('customer_type')}
                      </FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={value => {
                            field.onChange(value)
                            form.setValue('userId', '')
                            form.setValue('addressId', '')
                            form.setValue('firstname', '')
                            form.setValue('lastname', '')
                            form.setValue('email', '')
                            form.setValue('phone', '')
                            form.setValue('countryId', '')
                            form.setValue('stateId', '')
                            form.setValue('cityId', '')
                            form.setValue('zipCode', '')
                            form.setValue('addressText', '')
                          }}
                          className="grid grid-cols-2">
                          {(['registered', 'guest'] as const).map(type => (
                            <label
                              key={type}
                              className={cn(
                                'flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 shadow-xs transition-colors',
                                field.value === type ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                              )}>
                              <RadioGroupItem value={type} />
                              <span className="font-medium">{t(`customer_type_${type}`)}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Order Status */}
                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('order_status')}</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl className="w-full">
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.values(OrderStatusEnum).map(s => (
                            <SelectItem key={s} value={s}>
                              {t(`order_status_${s}` as any)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Currency */}
                <FormField
                  control={form.control}
                  name="currencyCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>{t('currency')}</FormLabel>
                      <Select
                        value={field.value ?? ''}
                        onValueChange={async value => {
                          const prevCode = field.value // capture old currency before update
                          field.onChange(value)
                          if (value === prevCode) {
                            return
                          }
                          const allItems = itemFields.map((item, i) => ({ item, i }))
                          if (allItems.length === 0) {
                            return
                          }
                          toast.info(t('refreshing_prices'))

                          // Always convert using fresh exchange rates — never re-fetch product prices
                          // (user may have manually overridden them)
                          const [ratesFrom, ratesTo] = await Promise.all([
                            fetchExchangeRates({
                              currencyCode: prevCode,
                              size: 1,
                              sortBy: 'effectiveDate',
                              sort: SortEnum.DESC
                            }).catch(() => ({ items: [] as { rate?: number }[] })),
                            fetchExchangeRates({
                              currencyCode: value,
                              size: 1,
                              sortBy: 'effectiveDate',
                              sort: SortEnum.DESC
                            }).catch(() => ({ items: [] as { rate?: number }[] }))
                          ])
                          const fromRate = (ratesFrom as { items: { rate?: number }[] }).items[0]?.rate ?? 1
                          const toRate = (ratesTo as { items: { rate?: number }[] }).items[0]?.rate ?? 1
                          if (fromRate === toRate) {
                            return
                          }
                          const factor = toRate / fromRate
                          allItems.forEach(({ i }) => {
                            const cur = form.getValues(`items.${i}.unitPriceOverride`)
                            if (cur !== null && cur !== undefined) {
                              form.setValue(`items.${i}.unitPriceOverride`, Math.round(cur * factor * 10000) / 10000)
                            }
                            const orig = form.getValues(`items.${i}.originalGross`)
                            if (orig !== null && orig !== undefined) {
                              form.setValue(`items.${i}.originalGross`, Math.round(orig * factor * 10000) / 10000)
                            }
                            const net = form.getValues(`items.${i}.unitNetPrice`)
                            if (net !== null && net !== undefined) {
                              form.setValue(`items.${i}.unitNetPrice`, Math.round(net * factor * 10000) / 10000)
                            }
                          })
                        }}>
                        <FormControl className="w-full">
                          <SelectTrigger>
                            <SelectValue placeholder="—" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeCurrencies.map(c => (
                            <SelectItem key={c.code} value={c.code}>
                              {c.symbol} {c.code} — {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {/* ── Card 2: Customer Info ─────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon className="h-5 w-5" />
                {t('customer_info')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {customerType === 'registered' ? (
                <>
                  {/* User Selection */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <FormField
                      control={form.control}
                      name="userId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required>{t('select_user')}</FormLabel>
                          <FormControl>
                            <UserCombobox
                              value={field.value || ''}
                              onValueChange={value => {
                                field.onChange(value)
                                form.setValue('addressId', '')
                              }}
                              error={!!fieldState.error}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Address Selection */}
                  {userId && (
                    <FormField
                      control={form.control}
                      name="addressId"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required>{t('shipping_address')}</FormLabel>
                          {isLoadingAddresses ? (
                            <p className="text-muted-foreground text-sm">{t('loading')}</p>
                          ) : addresses.length === 0 ? (
                            <p className="text-muted-foreground text-sm">{t('no_addresses')}</p>
                          ) : (
                            <FormControl>
                              <RadioGroup
                                value={field.value || ''}
                                onValueChange={field.onChange}
                                className={cn(
                                  'grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4',
                                  !!fieldState.error && 'ring-destructive/50 rounded-md p-2 ring-2'
                                )}>
                                {addresses.map(addr => (
                                  <label
                                    key={addr.id}
                                    className={cn(
                                      'flex cursor-pointer items-start gap-3 rounded-lg border p-4 shadow-xs transition-colors',
                                      field.value === addr.id
                                        ? 'border-primary bg-primary/5'
                                        : 'border-border hover:bg-accent'
                                    )}>
                                    <RadioGroupItem value={addr.id} className="mt-0.5 shrink-0" />
                                    <div className="min-w-0 space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="leading-none font-medium">{addr.title}</span>
                                        {addr.primary && (
                                          <Badge variant="secondary" className="text-xs">
                                            {t('primary_address')}
                                          </Badge>
                                        )}
                                      </div>
                                      <p className="text-muted-foreground truncate text-sm">
                                        {[addr.country?.name, addr.state?.name, addr.city?.name]
                                          .filter(Boolean)
                                          .join(' / ')}
                                      </p>
                                      {addr.zipCode && <p className="text-muted-foreground text-xs">{addr.zipCode}</p>}
                                      {addr.text && (
                                        <p className="text-muted-foreground line-clamp-2 text-xs">{addr.text}</p>
                                      )}
                                    </div>
                                  </label>
                                ))}
                              </RadioGroup>
                            </FormControl>
                          )}
                          <FormMessage />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="w-64"
                            onClick={() => setAddAddressOpen(true)}>
                            <PlusIcon className="h-4 w-4" />
                            {t('add_address')}
                          </Button>
                        </FormItem>
                      )}
                    />
                  )}
                </>
              ) : (
                <>
                  {/* Guest Info */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                    <FormField
                      control={form.control}
                      name="firstname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('firstname')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('enter_firstname')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastname"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('lastname')}</FormLabel>
                          <FormControl>
                            <Input placeholder={t('enter_lastname')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel required>{t('email')}</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder={t('enter_email')} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            {t('phone')} <span className="text-muted-foreground font-normal">({t('optional')})</span>
                          </FormLabel>
                          <FormControl>
                            <PhoneInput international value={field.value ?? ''} onChange={field.onChange} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Separator />

                  {/* Guest Address */}
                  <div>
                    <h3 className="mb-4 flex items-center gap-2 font-medium">
                      <MapPinIcon className="h-4 w-4" />
                      {t('shipping_address')}
                    </h3>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                      <FormField
                        control={form.control}
                        name="countryId"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel required>{t('country')}</FormLabel>
                            <FormControl>
                              <CountryCombobox
                                value={field.value || ''}
                                onValueChange={value => {
                                  field.onChange(value)
                                  form.setValue('stateId', '')
                                  form.setValue('cityId', '')
                                }}
                                valueField="id"
                                disabled={false}
                                error={!!fieldState.error}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="stateId"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel required>{t('state')}</FormLabel>
                            <FormControl>
                              <Combobox
                                options={stateOptions}
                                value={field.value || ''}
                                onValueChange={value => {
                                  field.onChange(value)
                                  form.setValue('cityId', '')
                                }}
                                placeholder={t('select')}
                                searchPlaceholder={t('search')}
                                emptyText={isLoadingStates ? t('loading') : t('not_found')}
                                disabled={isLoadingStates || !countryId}
                                clearable
                                error={!!fieldState.error}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="cityId"
                        render={({ field, fieldState }) => (
                          <FormItem>
                            <FormLabel required>{t('city')}</FormLabel>
                            <FormControl>
                              <Combobox
                                options={cityOptions}
                                value={field.value || ''}
                                onValueChange={field.onChange}
                                placeholder={t('select')}
                                searchPlaceholder={t('search')}
                                emptyText={isLoadingCities ? t('loading') : t('not_found')}
                                disabled={isLoadingCities || !stateId}
                                clearable
                                error={!!fieldState.error}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('zip_code')}</FormLabel>
                            <FormControl>
                              <Input placeholder={t('enter_zip_code')} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="addressText"
                        render={({ field }) => (
                          <FormItem className="md:col-span-2 lg:col-span-4">
                            <FormLabel required>{t('address_text')}</FormLabel>
                            <FormControl>
                              <Textarea rows={3} placeholder={t('enter_address_text')} {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* ── Card 3: Order Items ───────────────────────────────────── */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="flex items-center gap-2">
                <PackageIcon className="h-5 w-5" />
                {t('order_items')}
              </CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={() => setAddItemOpen(true)}>
                <PlusIcon className="h-4 w-4" />
                {t('add_item')}
              </Button>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="items"
                render={() => (
                  <FormItem>
                    <FormControl>
                      <div>
                        {itemFields.length === 0 ? (
                          <p className="text-muted-foreground py-6 text-center text-sm">{t('no_items_added')}</p>
                        ) : (
                          <div className="overflow-hidden rounded-md border">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead className="w-20" />
                                  <TableHead>{t('product')}</TableHead>
                                  <TableHead className="w-32">{t('quantity')}</TableHead>
                                  <TableHead className="w-40 text-right">
                                    {t('unit_price')}{' '}
                                    {currencySymbol && (
                                      <span className="text-muted-foreground font-normal">({currencySymbol})</span>
                                    )}
                                  </TableHead>
                                  <TableHead className="w-28 text-right">{t('discount_rate')}</TableHead>
                                  <TableHead className="w-36 text-right">
                                    {t('discounted_price')}{' '}
                                    {currencySymbol && (
                                      <span className="text-muted-foreground font-normal">({currencySymbol})</span>
                                    )}
                                  </TableHead>
                                  <TableHead className="w-36 text-right">
                                    {t('price_without_tax')}{' '}
                                    {currencySymbol && (
                                      <span className="text-muted-foreground font-normal">({currencySymbol})</span>
                                    )}
                                  </TableHead>
                                  <TableHead className="w-36 text-right">
                                    {t('price_with_tax')}{' '}
                                    {currencySymbol && (
                                      <span className="text-muted-foreground font-normal">({currencySymbol})</span>
                                    )}
                                  </TableHead>
                                  <TableHead className="w-28 text-right">{t('tax_rate')}</TableHead>
                                  <TableHead className="w-28 text-center">{t('price_includes_tax')}</TableHead>
                                  <TableHead className="w-36 text-right">
                                    {t('subtotal')}{' '}
                                    {currencySymbol && (
                                      <span className="text-muted-foreground font-normal">({currencySymbol})</span>
                                    )}
                                  </TableHead>
                                  <TableHead className="w-10" />
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {itemFields.map((item, index) => {
                                  const row = watchedItems[index] ?? item
                                  return (
                                    <TableRow key={item.id}>
                                      <TableCell className="py-2">
                                        <div
                                          className="relative w-16 overflow-hidden rounded border"
                                          style={{ aspectRatio: '4/3' }}>
                                          {item.mainPicturePath ? (
                                            <Image
                                              src={getResizedPictureUrl(item.mainPicturePath, 80, 60)}
                                              alt={item.productCode}
                                              fill
                                              className="object-cover"
                                            />
                                          ) : (
                                            <div className="bg-muted flex h-full w-full items-center justify-center">
                                              <ImageIcon className="text-muted-foreground h-5 w-5" />
                                            </div>
                                          )}
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex flex-col">
                                          <span className="font-medium">{item.productCode}</span>
                                          <span className="text-muted-foreground text-sm">{item.productTitle}</span>
                                        </div>
                                      </TableCell>
                                      <TableCell>
                                        <NumberInput
                                          min={1}
                                          stepper={1}
                                          value={row.quantity}
                                          onValueChange={v => form.setValue(`items.${index}.quantity`, v ?? 1)}
                                        />
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <MoneyInput
                                          currencySymbol={currencySymbol}
                                          value={row.originalGross ?? row.unitPriceOverride}
                                          onValueChange={v => {
                                            form.setValue(`items.${index}.originalGross`, v)
                                            const discountRate =
                                              form.getValues(`items.${index}.discountRateOverride`) ?? 0
                                            const discounted =
                                              v !== undefined && discountRate > 0 ? v * (1 - discountRate / 100) : v
                                            form.setValue(`items.${index}.unitPriceOverride`, discounted)
                                            form.setValue(`items.${index}.priceManuallyOverridden`, true)
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <NumberInput
                                          min={0}
                                          max={100}
                                          stepper={0.5}
                                          decimalScale={2}
                                          suffix="%"
                                          value={row.discountRateOverride ?? 0}
                                          onValueChange={v => {
                                            form.setValue(`items.${index}.discountRateOverride`, v)
                                            const origGross =
                                              form.getValues(`items.${index}.originalGross`) ??
                                              form.getValues(`items.${index}.unitPriceOverride`) ??
                                              0
                                            const isManual = form.getValues(`items.${index}.priceManuallyOverridden`)
                                            if (!isManual && origGross > 0 && v !== undefined) {
                                              form.setValue(
                                                `items.${index}.unitPriceOverride`,
                                                origGross * (1 - v / 100)
                                              )
                                            }
                                          }}
                                          inputClassName="text-right"
                                        />
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <div className="text-sm font-medium">
                                          {itemDisplayData[index].discountedGross.toFixed(2)}{' '}
                                          {currencySymbol && (
                                            <span className="text-muted-foreground font-normal">{currencySymbol}</span>
                                          )}
                                        </div>
                                        {itemDisplayData[index].discountAmount > 0 && (
                                          <div className="text-xs text-green-600">
                                            -{itemDisplayData[index].discountAmount.toFixed(2)} {currencySymbol ?? ''}
                                          </div>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <span className="text-sm font-medium">
                                          {netUnitPrices[index]?.toFixed(2)} {currencySymbol ?? ''}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <span className="text-sm font-medium">
                                          {itemDisplayData[index].finalGross?.toFixed(2)} {currencySymbol ?? ''}
                                        </span>
                                      </TableCell>
                                      <TableCell className="text-right align-middle">
                                        <NumberInput
                                          min={0}
                                          max={100}
                                          stepper={0.5}
                                          decimalScale={2}
                                          suffix="%"
                                          value={row.taxRate ?? 0}
                                          onValueChange={v => form.setValue(`items.${index}.taxRate`, v)}
                                          inputClassName="text-right"
                                        />
                                      </TableCell>
                                      <TableCell className="text-center align-middle">
                                        <Switch
                                          checked={row.priceIncludesTax ?? true}
                                          onCheckedChange={v => {
                                            const prev = form.getValues(`items.${index}.priceIncludesTax`) ?? true
                                            if (prev === v) {
                                              return
                                            }
                                            const taxRate = form.getValues(`items.${index}.taxRate`) ?? 0
                                            form.setValue(`items.${index}.priceIncludesTax`, v)
                                            if (taxRate <= 0) {
                                              return
                                            }
                                            const origGross = form.getValues(`items.${index}.originalGross`)
                                            const unitPrice = form.getValues(`items.${index}.unitPriceOverride`) ?? 0
                                            if (!v) {
                                              if (origGross) {
                                                form.setValue(
                                                  `items.${index}.originalGross`,
                                                  Math.round((origGross / (1 + taxRate / 100)) * 10000) / 10000
                                                )
                                              }
                                              if (unitPrice) {
                                                form.setValue(
                                                  `items.${index}.unitPriceOverride`,
                                                  Math.round((unitPrice / (1 + taxRate / 100)) * 10000) / 10000
                                                )
                                              }
                                            } else {
                                              if (origGross) {
                                                form.setValue(
                                                  `items.${index}.originalGross`,
                                                  Math.round(origGross * (1 + taxRate / 100) * 10000) / 10000
                                                )
                                              }
                                              if (unitPrice) {
                                                form.setValue(
                                                  `items.${index}.unitPriceOverride`,
                                                  Math.round(unitPrice * (1 + taxRate / 100) * 10000) / 10000
                                                )
                                              }
                                            }
                                          }}
                                        />
                                      </TableCell>
                                      <TableCell className="text-right font-medium">
                                        {subtotals[index]?.toFixed(2)}{' '}
                                        {currencySymbol && (
                                          <span className="text-muted-foreground text-sm font-normal">
                                            {currencySymbol}
                                          </span>
                                        )}
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="text-destructive hover:text-destructive h-9 w-9"
                                          onClick={() => setDeleteItemIndex(index)}>
                                          <Trash2Icon className="h-4 w-4" />
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  )
                                })}
                              </TableBody>
                            </Table>
                            {/* ── Discount + Totals ── */}
                            <div className="space-y-2 border-t px-4 py-3">
                              <div className="flex items-center justify-between gap-4">
                                <FormField
                                  control={form.control}
                                  name="discountRate"
                                  render={({ field }) => (
                                    <FormItem className="flex items-center gap-3 space-y-0">
                                      <FormLabel className="shrink-0 text-sm">{t('discount_rate')} (%)</FormLabel>
                                      <FormControl>
                                        <NumberInput
                                          min={0}
                                          max={100}
                                          stepper={0.5}
                                          decimalScale={2}
                                          fixedDecimalScale
                                          value={field.value}
                                          onValueChange={v => field.onChange(v)}
                                          placeholder="0.00"
                                          suffix="%"
                                          className="w-36"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                                <div className="space-y-1 text-right text-sm">
                                  <div className="flex justify-between gap-8">
                                    <span className="text-muted-foreground">{t('items_total')}</span>
                                    <span className="font-medium">
                                      {itemsTotal.toFixed(2)} {currencySymbol}
                                    </span>
                                  </div>
                                  {(watchedDiscountRate ?? 0) > 0 && (
                                    <div className="flex justify-between gap-8 text-orange-600">
                                      <span>
                                        {t('discount')} ({watchedDiscountRate}%)
                                      </span>
                                      <span>
                                        -{discountAmount.toFixed(2)} {currencySymbol}
                                      </span>
                                    </div>
                                  )}
                                  <div className="flex justify-between gap-8 border-t pt-1">
                                    <span className="text-muted-foreground">{t('subtotal')}</span>
                                    <span>
                                      {netTotal.toFixed(2)} {currencySymbol}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-8">
                                    <span className="text-muted-foreground">{t('tax_total')}</span>
                                    <span>
                                      {taxTotal.toFixed(2)} {currencySymbol}
                                    </span>
                                  </div>
                                  <div className="flex justify-between gap-8 border-t pt-1">
                                    <span className="font-semibold">{t('grand_total')}</span>
                                    <span className="font-semibold">
                                      {grandTotal.toFixed(2)} {currencySymbol}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* ── Card 4: Note ──────────────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <StickyNoteIcon className="h-5 w-5" />
                {t('note')} <span className="text-muted-foreground text-sm font-normal">({t('optional')})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea placeholder={t('note_placeholder')} rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="noteIsPublic"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-3">
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="mt-0!">{t('visible_to_customer')}</FormLabel>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* ── Card 5: Notifications ─────────────────────────────────── */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MailIcon className="h-5 w-5" />
                {t('notifications')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-6">
                <FormField
                  control={form.control}
                  name="notifyCustomer"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center gap-3">
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="mt-0!">{t('notify_customer_by_email')}</FormLabel>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {customerType === 'guest' && (
                  <FormField
                    control={form.control}
                    name="showPrices"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center gap-3">
                        <FormControl>
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                        <FormLabel className="mt-0!">{t('show_prices_to_customer')}</FormLabel>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
                <FormField
                  control={form.control}
                  name="locale"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('language')}</FormLabel>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder={defaultLocaleCode} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {activeLocales.map(locale => (
                            <SelectItem key={locale.fullCode} value={locale.fullCode}>
                              {locale.name} ({locale.fullCode})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* ── Footer ────────────────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-3">
            <Button type="button" variant="outline" asChild>
              <Link href="/orders">
                <XIcon />
                {t('cancel')}
              </Link>
            </Button>
            <Button type="submit" disabled={isPending}>
              <SaveIcon />
              {isPending ? t('saving') : t('create_order')}
            </Button>
          </div>
          <UnsavedChangesGuardDialog
            open={isNavigationBlocked}
            onConfirm={confirmNavigation}
            onCancel={cancelNavigation}
          />
        </form>
      </Form>

      {/* ── Dialogs ───────────────────────────────────────────────────── */}
      <AddOrderItemDialog
        open={addItemOpen}
        onOpenChange={setAddItemOpen}
        currencySymbol={currencySymbol}
        currencyCode={selectedCurrencyCode}
        onAddItems={items => {
          items.forEach(item => {
            const existingIndex = item.productId ? itemFields.findIndex(f => f.productId === item.productId) : -1
            if (existingIndex >= 0) {
              const existing = form.getValues(`items.${existingIndex}`)
              form.setValue(`items.${existingIndex}.quantity`, existing.quantity + item.quantity)
              if (item.priceManuallyOverridden) {
                form.setValue(`items.${existingIndex}.unitPriceOverride`, item.unitPriceOverride)
                form.setValue(`items.${existingIndex}.priceManuallyOverridden`, true)
              }
              if (item.discountRateOverride !== undefined) {
                form.setValue(`items.${existingIndex}.discountRateOverride`, item.discountRateOverride)
              }
            } else {
              appendItem({
                productId: item.productId,
                productCode: item.productCode,
                productTitle: item.productTitle,
                mainPicturePath: item.mainPicturePath,
                quantity: item.quantity,
                unitPriceOverride: item.unitPriceOverride,
                discountRateOverride: item.discountRateOverride,
                unitNetPrice: item.unitNetPrice,
                originalGross: item.originalGross,
                taxRate: item.taxRate,
                priceIncludesTax: item.priceIncludesTax,
                priceManuallyOverridden: item.priceManuallyOverridden ?? false
              })
            }
          })
        }}
      />

      {userId && (
        <AddressFormDialog
          open={addAddressOpen}
          onOpenChange={setAddAddressOpen}
          mode={FormModeEnum.CREATE}
          isLoading={isCreatingAddress}
          onSubmit={
            handleCreateAddress as (
              data: import('@/types/address').CreateAddressParams | import('@/types/address').UpdateAddressParams
            ) => void
          }
        />
      )}

      <DeleteConfirmationDialog
        open={deleteItemIndex !== null}
        onCancel={() => setDeleteItemIndex(null)}
        onConfirm={() => {
          if (deleteItemIndex !== null) {
            removeItem(deleteItemIndex)
          }
          setDeleteItemIndex(null)
        }}
      />
    </div>
  )
}

export default OrderCreate
